import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Check, Loader2, RotateCcw } from 'lucide-react';
import type { SetOccurrenceStatusInput, TaskReviewItem, TaskType } from '@pms/shared';
import { SearchInput } from '@/components/ui/search-input';
import { Button } from '@/components/ui/button';
import { getErrorMessage } from '@/lib/errors';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { formatDate, formatRelativeTime } from '@/lib/dateFormat';
import { tasksService } from '@/services/tasks.service';
import { departmentsService } from '@/services/departments.service';
import { TaskTypeBadge } from '@/components/tasks/TaskBadges';
import { TaskReviewModal } from '@/components/tasks/TaskReviewModal';
import { StatusRemarkModal } from '@/components/tasks/StatusRemarkModal';
import type { VoiceNoteDraft } from '@/components/tasks/VoiceNoteRecorder';
import { REVIEWABLE_TYPES, TYPE_META, fullName, initials } from '@/components/tasks/taskConstants';

interface ReviewRowProps {
  item: TaskReviewItem;
  onOpen: () => void;
  onDone: () => void;
  onNeedsImprovement: () => void;
  busy: boolean;
}

/**
 * One submission, with both verdicts inline.
 *
 * The row is NOT one big button: nesting the action buttons inside a button is invalid
 * HTML and breaks their click targets. Instead a clickable region opens the detail modal
 * and the actions sit beside it as siblings.
 */
function ReviewRow({ item, onOpen, onDone, onNeedsImprovement, busy }: ReviewRowProps) {
  return (
    <div className="flex items-center gap-3 border-b border-border/60 px-4 py-3 transition-colors last:border-b-0 hover:bg-muted/40">
      <button
        type="button"
        onClick={onOpen}
        className="flex min-w-0 flex-1 items-center gap-3 text-left"
      >
        {/* The assignee leads the row: a reviewer scans by person, not by task title. */}
        {item.photo_url ? (
          <img src={item.photo_url} alt="" className="h-9 w-9 shrink-0 rounded-full object-cover" />
        ) : (
          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary/10 text-[11px] font-semibold text-primary">
            {initials(item.first_name, item.last_name)}
          </span>
        )}

        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
            <span className="truncate text-sm font-semibold text-foreground">
              {fullName(item.first_name, item.last_name)}
            </span>
            {item.is_priority && (
              <span className="rounded-full bg-rose-100 px-2 py-0.5 text-[10px] font-semibold text-rose-700 dark:bg-rose-500/15 dark:text-rose-400">
                Priority
              </span>
            )}
          </div>
          <p className="truncate text-sm text-muted-foreground">{item.task_title}</p>
        </div>

        <div className="hidden shrink-0 flex-col items-end gap-1 sm:flex">
          <TaskTypeBadge type={item.task_type} />
          <span className="text-[11px] text-muted-foreground">
            {item.submitted_at ? formatRelativeTime(item.submitted_at) : formatDate(item.occurrence_date)}
          </span>
        </div>
      </button>

      <div className="flex shrink-0 items-center gap-2">
        <Button size="sm" variant="outline" disabled={busy} onClick={onNeedsImprovement}>
          <RotateCcw className="h-4 w-4" />
          <span className="hidden md:inline">Need Improvement</span>
        </Button>
        <Button size="sm" disabled={busy} onClick={onDone}>
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
          Done
        </Button>
      </div>
    </div>
  );
}

/**
 * The reviewer's queue: everything submitted on tasks this user created.
 *
 * Rows are occurrence-assignee submissions rather than tasks — the same task on the
 * same date can be submitted by several people, and each is approved separately.
 */
export function ReviewTab() {
  const [search, setSearch] = useState('');
  const [dept, setDept] = useState('all');
  const [type, setType] = useState('all');
  const [page, setPage] = useState(1);
  const [active, setActive] = useState<TaskReviewItem | null>(null);
  // The row whose Need Improvement remark is being collected — separate from `active`,
  // which is the detail modal, so acting from a row never opens the detail view.
  const [remarkFor, setRemarkFor] = useState<TaskReviewItem | null>(null);

  const qc = useQueryClient();
  const review = useMutation({
    mutationFn: ({ id, input, voice }: {
      id: string; input: SetOccurrenceStatusInput; voice?: VoiceNoteDraft | null;
    }) => tasksService.setStatusWithVoice(id, input, voice ?? null),
    onSuccess: (result, { input }) => {
      // A failed recording must not read as a failed review — the verdict already stands.
      if (result.voiceError) {
        toast.warning(`Sent back, but the voice note failed to upload. (${result.voiceError})`);
      } else {
        toast.success(
          input.status === 'completed'
            ? 'Task approved and marked completed'
            : 'Sent back to the assignee for improvement',
        );
      }
      setRemarkFor(null);
      // Refreshes the list and the tab's count badge, which share the 'tasks' prefix.
      qc.invalidateQueries({ queryKey: ['tasks'] });
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const deptsQ = useQuery({
    queryKey: ['departments', 'all'],
    queryFn: () => departmentsService.list({ limit: 100 }),
  });

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['tasks', 'review', { search, dept, type, page }],
    queryFn: () => tasksService.reviewQueue({
      q: search.trim() || undefined,
      department_id: dept === 'all' ? undefined : dept,
      task_type: type === 'all' ? undefined : (type as TaskType),
      page,
      limit: 20,
    }),
  });

  function resetPage<T>(setter: (v: T) => void) {
    return (v: T) => { setter(v); setPage(1); };
  }

  const total = data?.pagination.total ?? 0;
  const totalPages = data?.pagination.totalPages ?? 1;

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-3 rounded-xl border border-border/60 bg-card p-4 lg:flex-row">
        <SearchInput
          value={search}
          onChange={resetPage(setSearch)}
          placeholder="Search tasks or assignees…"
          containerClassName="flex-1"
        />
        <Select value={dept} onValueChange={resetPage(setDept)}>
          <SelectTrigger className="lg:w-48"><SelectValue placeholder="All Departments" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Departments</SelectItem>
            {(deptsQ.data?.data ?? []).map((d) => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}
          </SelectContent>
        </Select>
        {/* Only reviewable types can appear here, so daily is left out of the filter. */}
        <Select value={type} onValueChange={resetPage(setType)}>
          <SelectTrigger className="lg:w-40"><SelectValue placeholder="All Types" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Types</SelectItem>
            {REVIEWABLE_TYPES.map((t) => <SelectItem key={t} value={t}>{TYPE_META[t].label}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <p className="text-sm text-muted-foreground">
        {total} task{total === 1 ? '' : 's'} awaiting your review
      </p>

      <div className="overflow-hidden rounded-xl border border-border/60 bg-card">
        {isLoading && (
          <div className="space-y-px p-1">
            {[...Array(6)].map((_, i) => <div key={i} className="h-16 animate-pulse rounded bg-muted/40" />)}
          </div>
        )}
        {isError && (
          <div className="p-10 text-center text-sm text-muted-foreground">
            Couldn’t load the review queue.{' '}
            <button className="text-primary underline" onClick={() => refetch()}>Retry</button>
          </div>
        )}
        {data && data.data.length === 0 && (
          <div className="p-10 text-center text-sm text-muted-foreground">
            Nothing waiting on your review right now.
          </div>
        )}
        {data?.data.map((item) => (
          <ReviewRow
            key={item.id}
            item={item}
            busy={review.isPending && review.variables?.id === item.id}
            onOpen={() => setActive(item)}
            onDone={() => review.mutate({ id: item.id, input: { status: 'completed' } })}
            onNeedsImprovement={() => setRemarkFor(item)}
          />
        ))}
      </div>

      {totalPages > 1 && (
        <div className="flex items-center justify-between text-sm">
          <span className="text-muted-foreground">Page {page} of {totalPages} · {total} total</span>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>Previous</Button>
            <Button size="sm" variant="outline" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>Next</Button>
          </div>
        </div>
      )}

      <TaskReviewModal item={active} onOpenChange={(o) => !o && setActive(null)} />

      {remarkFor && (
        <StatusRemarkModal
          status="needs_improvement"
          open
          onOpenChange={(o) => { if (!o) setRemarkFor(null); }}
          busy={review.isPending}
          onConfirm={(note, voice) =>
            review.mutate({ id: remarkFor.id, input: { status: 'needs_improvement', note }, voice })}
        />
      )}
    </div>
  );
}
